// Utility functions for field mapping and CSV processing
export interface FieldMapping {
  originalName: string;
  mappedName: string;
  confidence: number;
  isRequired: boolean;
}

export interface CSVDialect {
  delimiter: string;
  encoding: string;
  hasHeader: boolean;
}

export interface SchemaConfig {
  required_min: string[];
  rules_requirements: Record<string, string[]>;
  optional: string[];
}

// Schema configuration for tax account module
export const SKATTEKONTO_SCHEMA: SchemaConfig = {
  required_min: ["Datum", "Händelse", "Belopp"],
  rules_requirements: {
    "Försenad inbetalning": ["Förfallodatum"],
    "Deklarerat vs bokfört": ["DeklareratBelopp"],
    "Avgift/ränta utan skuld": ["Händelse"],
    "Oregelbunden preliminärskatt": ["Händelse"]
  },
  optional: ["Saldo", "Referens", "Typ", "Kommentar", "TransaktionsID"]
};

// Field synonyms for automatic mapping suggestions
const FIELD_SYNONYMS: Record<string, string[]> = {
  "Datum": [
    "datum", "date", "bokföringsdag", "bokforingsdag", "transaktionsdatum", 
    "transaction_date", "bokförd", "bokford", "posting_date"
  ],
  "Händelse": [
    "händelse", "handelse", "event", "typ", "type", "transaction_type", 
    "text", "beskrivning", "description", "transaktion", "transaction"
  ],
  "Belopp": [
    "belopp", "amount", "summa", "sum", "belopp_sek", "amount_sek", 
    "kronor", "kr", "värde", "varde", "value"
  ],
  "Förfallodatum": [
    "förfallodatum", "forfallodatum", "due_date", "duedate", "förfallodatum", 
    "förfall", "forfall", "due", "deadline", "förf.datum", "förf_datum"
  ],
  "DeklareratBelopp": [
    "deklarerat", "declared", "deklarerat_belopp", "declared_amount", 
    "ska_betalas", "expected", "förväntat", "forvantat", "budgeterat"
  ],
  "Saldo": [
    "saldo", "balance", "balans", "balanse", "kvarvarande", "remaining", 
    "skuld", "debt", "total", "balance_amount", "account_balance"
  ],
  "Referens": [
    "referens", "reference", "ref", "id", "ärendenummer", "arendenummer", 
    "case_id", "reference_id", "löpnummer", "lopnummer"
  ],
  "TransaktionsID": [
    "transaktionsid", "transaction_id", "trans_id", "id", "unique_id", 
    "rad_id", "row_id", "nummer", "number"
  ]
};

/**
 * Auto-detect CSV dialect (delimiter, encoding, header)
 */
export function sniffCSVDialect(content: string): CSVDialect {
  const lines = content.split('\n').slice(0, 5);
  
  // Detect delimiter
  let delimiter = ',';
  const semicolonCount = lines[0]?.split(';').length || 0;
  const commaCount = lines[0]?.split(',').length || 0;
  const tabCount = lines[0]?.split('\t').length || 0;
  
  if (semicolonCount > commaCount && semicolonCount > tabCount) {
    delimiter = ';';
  } else if (tabCount > commaCount && tabCount > semicolonCount) {
    delimiter = '\t';
  }
  
  // Detect header
  const firstLine = lines[0] || '';
  const hasHeader = /[a-zA-ZåäöÅÄÖ]/.test(firstLine) && 
                   !(/^\d{4}-\d{2}-\d{2}/.test(firstLine));
  
  return {
    delimiter,
    encoding: 'utf-8',
    hasHeader
  };
}

/**
 * Parse CSV content into headers and preview rows
 */
export function parseCSVForMapping(content: string, dialect: CSVDialect): {
  headers: string[];
  preview: string[][];
} {
  const lines = content
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .filter(line => line.trim().length > 0);
  
  if (lines.length === 0) return { headers: [], preview: [] };
  
  const parseRow = (line: string) => 
    line.split(dialect.delimiter).map(cell => cell.trim().replace(/^"|"$/g, ''));
  
  const headers = dialect.hasHeader ? parseRow(lines[0]) : 
    parseRow(lines[0]).map((_, i) => `Column ${i + 1}`);
  
  const startIndex = dialect.hasHeader ? 0 : 0;
  const preview = lines.slice(startIndex, Math.min(startIndex + 10, lines.length))
    .map(parseRow);
  
  return { headers, preview };
}

/**
 * Suggest field mappings based on synonyms and fuzzy matching
 */
export function suggestFieldMapping(headers: string[], schema: SchemaConfig): FieldMapping[] {
  const mappings: FieldMapping[] = [];
  
  const normalize = (s: string) => 
    s.normalize('NFD')
     .replace(/\p{Diacritic}/gu, '')
     .toLowerCase()
     .trim();
  
  for (const header of headers) {
    const normalizedHeader = normalize(header);
    let bestMatch = { field: '', confidence: 0 };
    
    // Test against all synonym groups
    for (const [targetField, synonyms] of Object.entries(FIELD_SYNONYMS)) {
      for (const synonym of synonyms) {
        const normalizedSynonym = normalize(synonym);
        
        // Exact match gets highest score
        if (normalizedHeader === normalizedSynonym) {
          bestMatch = { field: targetField, confidence: 1.0 };
          break;
        }
        
        // Partial match
        if (normalizedHeader.includes(normalizedSynonym) || 
            normalizedSynonym.includes(normalizedHeader)) {
          const similarity = Math.min(normalizedHeader.length, normalizedSynonym.length) / 
                           Math.max(normalizedHeader.length, normalizedSynonym.length);
          if (similarity > bestMatch.confidence) {
            bestMatch = { field: targetField, confidence: similarity * 0.8 };
          }
        }
        
        // Fuzzy matching with Levenshtein-like heuristic
        const distance = levenshteinDistance(normalizedHeader, normalizedSynonym);
        const maxLen = Math.max(normalizedHeader.length, normalizedSynonym.length);
        const similarity = 1 - (distance / maxLen);
        
        if (similarity > 0.6 && similarity > bestMatch.confidence) {
          bestMatch = { field: targetField, confidence: similarity * 0.6 };
        }
      }
      
      if (bestMatch.confidence === 1.0) break;
    }
    
    // Determine if field is required
    const isRequired = schema.required_min.includes(bestMatch.field) ||
                      Object.values(schema.rules_requirements).some(reqs => 
                        reqs.includes(bestMatch.field));
    
    mappings.push({
      originalName: header,
      mappedName: bestMatch.field || 'unmapped',
      confidence: bestMatch.confidence,
      isRequired
    });
  }
  
  return mappings.sort((a, b) => b.confidence - a.confidence);
}

/**
 * Validate that required fields are present in mappings
 */
export function validateFieldMapping(mappings: FieldMapping[], schema: SchemaConfig): {
  isValid: boolean;
  missingRequired: string[];
  missingRecommended: string[];
  availableRules: string[];
  skippedRules: string[];
} {
  const mappedFields = new Set(
    mappings
      .filter(m => m.mappedName !== 'unmapped' && m.confidence > 0.1)
      .map(m => m.mappedName)
  );
  
  // Check required_min
  const missingRequired = schema.required_min.filter(req => !mappedFields.has(req));
  
  // Check recommended fields (Saldo is highly recommended for balance checks)
  const recommendedFields = ['Saldo'];
  const missingRecommended = recommendedFields.filter(
    field => !mappedFields.has(field)
  );
  
  // Check which rules can run
  const availableRules: string[] = [];
  const skippedRules: string[] = [];
  
  for (const [ruleName, requirements] of Object.entries(schema.rules_requirements)) {
    const hasAllRequirements = requirements.every(req => mappedFields.has(req));
    
    if (hasAllRequirements) {
      availableRules.push(ruleName);
    } else {
      skippedRules.push(ruleName);
    }
  }
  
  return {
    isValid: missingRequired.length === 0,
    missingRequired,
    missingRecommended,
    availableRules,
    skippedRules
  };
}

/**
 * Simple Levenshtein distance for fuzzy matching
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
  
  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,     // deletion
        matrix[j - 1][i] + 1,     // insertion
        matrix[j - 1][i - 1] + cost // substitution
      );
    }
  }
  
  return matrix[str2.length][str1.length];
}